import React from "react";
import { useEffect, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useHistory } from "react-router-dom";
import TowerIcon from "../assets/icons/TowerIcon.svg";
import TowerIconYellow from "../assets/icons/TowerIconYellow.svg";
import TowerIconGreen from "../assets/icons/TowerIconGreen.svg";
import TowerIconGray from "../assets/icons/TowerIconGray.svg";
import * as API from "../../api.js";

const statusColors = {
  pending: "#FACC15", // yellow
  completed: "#01B574", // green
  inProgress: "#4E4AFF", // blue
  null: "#9CA3AF", // gray
};

const statusIcons = {
  pending: TowerIconYellow,
  completed: TowerIconGreen,
  inProgress: TowerIcon,
  null: TowerIconGray,
};

export function TowerLocations() {
  const history = useHistory();
  const [theme, setTheme] = useState(
    document.documentElement.classList.contains("dark") ? "dark" : "light"
  );
  const [towersData, setTowersData] = useState([]);
  const [mapImages, setMapImages] = useState([]);
  const [map, setMap] = useState(null);

  useEffect(() => {
    const fetchTowers = async () => {
      try {
        const response = await API.getAllProjects(
          " ",
          10000,
          0,
          null,
          "PicStork",
          null,
          null,
          "created_date",
          "DESC"
        );

        if (!response?.projects) return;

        const towers = response.projects.map((item, index) => ({
          id: item.id || index + 1,
          collection_id: item.collection_id,
          collection_name: item.project_name || `Collection ${index + 1}`,
          current_status: item.current_status,
          project_data: item,
        }));

        setTowersData(towers);
      } catch (err) {
        console.error(err);
      }
    };

    fetchTowers();
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(
        document.documentElement.classList.contains("dark") ? "dark" : "light"
      );
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!towersData.length) return;

    const fetchTowerImages = async () => {
      try {
        const towerImages = [];

        for (const tower of towersData) {
          const res = await API.getImageCollectionImages(
            tower.collection_id,
            " ",
            1,
            1
          );

          if (Array.isArray(res?.images) && res.images.length > 0) {
            const img = res.images[0];
            if (img.latitude && img.longitude) {
              towerImages.push({
                ...img,
                collection_name: tower.collection_name,
                current_status: tower.current_status,
                project_data: tower.project_data,
              });
            }
          }
        }

        setMapImages(towerImages);
      } catch (err) {
        console.error("Error fetching tower images:", err);
      }
    };

    fetchTowerImages();
  }, [towersData]);

  useEffect(() => {
    if (map) return;

    const mapInstance = L.map("solar-map", {
      zoomControl: false,
      minZoom: 3,
      maxZoom: 20,
    });

    const tileLayer = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
        subdomains: "abcd",
        maxZoom: 20,
      }
    );

    tileLayer.addTo(mapInstance);
    mapInstance.setView([20.5937, 78.9629], 5);

    setMap(mapInstance);
  }, [map]);

  const handleTowerClick = async (towerData) => {
   console.log("Tower clicked:", towerData);

   history.push(`/picstork/tower-plans`, {
     rowData: towerData.project_data,
   });
  };

  useEffect(() => {
    if (!map) return;

    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) map.removeLayer(layer);
    });

    if (!mapImages.length) {
      map.setView([20.5937, 78.9629], 5);
      return;
    }

    const bounds = L.latLngBounds([]);

    mapImages.forEach((img) => {
      const lat = parseFloat(img.latitude);
      const lng = parseFloat(img.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      const color = statusColors[img.current_status] || statusColors.null;
      const iconSrc = statusIcons[img.current_status] || TowerIconGray;

      const customIcon = L.divIcon({
        html: `
          <div style="display: flex; flex-direction: column; align-items: center; cursor: pointer;">
            <img src="${iconSrc}" alt="Tower Icon" style="width: 28px; height: 28px;" />
            <span style="
              font-size: 12px;
              margin-top: 4px;
              background: ${color};
              color: white;
              padding: 2px 6px;
              border-radius: 4px;
              white-space: nowrap;
            ">
              ${img.collection_name}
            </span>
          </div>
        `,
        className: "custom-marker",
        iconSize: [40, 40],
        iconAnchor: [20, 40],
      });

      const marker = L.marker([lat, lng], { icon: customIcon })
        .addTo(map)
        .on("click", () => {
          handleTowerClick(img);
        });

      bounds.extend([lat, lng]);
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 15,
      });
    }
  }, [mapImages, map, theme]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div
        id="solar-map"
        style={{
          width: "100%",
          height: "100%",
          cursor: "pointer",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "20%",
          background: "linear-gradient(to bottom, white, transparent)",
          pointerEvents: "none",
          zIndex: 500,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "20%",
          background: "linear-gradient(to top, white, transparent)",
          pointerEvents: "none",
          zIndex: 500,
        }}
      />

      <div
        style={{
          position: "absolute",
          top: "0.4rem",
          left: "0.4rem",
          color: "black",
          padding: "0.25rem 0.75rem",
          borderRadius: "0.5rem",
          zIndex: 1000,
        }}
      >
        <p style={{ margin: "0px", fontWeight: 500, color:"#18242D" }}>Location of Towers</p>
      </div>

      <div
        style={{
          position: "absolute",
          bottom: "0.4rem",
          right: "0.4rem",
          color: "black",
          padding: "0.5rem",
          zIndex: 1000,
          display: "flex",
          gap: "0.5rem",
          fontSize: "0.8rem",
        }}
      >
        {[
          { key: "completed", label: "Completed" },
          { key: "inProgress", label: "In Progress" },
          { key: "pending", label: "Pending" }
        ].map((item) => (
          <div
            key={item.key}
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            <span
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: statusColors[item.key],
                display: "inline-block",
              }}
            ></span>
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
